import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { uniqBy, isEmpty, omitBy, isNil, isNull, isArray } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { decode, encode, routeToBoxesView, toaster } from "Helpers/index";
import useSession from "Hooks/session.hook";
//ANTD
import { Col, Row, Tooltip } from "antd";

// SHARED COMPONENT
import DataTableFilter from "Components/shared/data-table-filter";
import { DataTable, Button, BasicModal } from "@life-line-lab/components-react";
import AssignDriverModal from "Components/delivery-management/parcel-tracking-modals/assign-driver-modal/assign-driver-modal";
import AssignDriverBoxesModal from "Components/delivery-management/parcel-tracking-modals/sorting-modal/assign-driver-boxes-modal";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { getStatusStyle } from "Helpers/get-status-colours";
export interface Option {
  label: string;
  value: string;
}

interface SortingBoxesListProps {
  onFilter?: any;
  customerOptions?: any;
  vehicleOptions?: any;
  sortingHubOptions?: any;
  allOrderList?: any;
  onAssignDriver?: any;
  handoverPointOptions?: any;
  driverOptions?: any;
}

const SortingBoxesList: React.FC<SortingBoxesListProps> = ({
  onFilter,
  customerOptions,
  vehicleOptions,
  sortingHubOptions,
  handoverPointOptions,
  allOrderList,
  onAssignDriver,
  driverOptions,
}) => {
  const { org, roleTypes } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const initialModal: any = {
    open: false,
  };

  const [selectedBoxRows, setSelectedBoxRows] = useState<any>([]);
  const [selectedBoxInfo, setSelectedBoxInfo] = useState<any>([]);
  const [modal, setModal] = useState<any>(initialModal);
  const [callModal, setCallModal] = useState<any>(<></>);
  // const [handoverPoints, setHandoverPoints] = useState<any>([])

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  // const rowSelection = {
  //   onChange: (selectedRows: any[]) => {
  //     setSelectedBoxRows(selectedRows);
  //   },
  //   getCheckboxProps: (record) => ({
  //     disabled: record.deliveryType === "Mid-Mile Only" || record.deliveryType === "First-Mile + Mid-Mile",
  //   }),
  // };

  const rowSelection =
    roleTypes !== "CUSTOMER-SUPER_ADMIN"
      ? {
          onChange: (selectedRows: any[]) => {
            setSelectedBoxRows(selectedRows);
          },
          getCheckboxProps: (record) => ({
            disabled:
              record.deliveryType === "Mid-Mile Only" ||
              record.deliveryType === "First-Mile + Mid-Mile",
          }),
        }
      : undefined;

  const handleOpenBoxesTrackingView = (boxID: string) => {
    routeToBoxesView(boxID, location, history)
  };

  const handleBoxTrolleyPage = () => {
    history.push(`/fullscreen/tracking`);
  };

  const handleOpenAssignDriver = () => {
    let boxDetails: any = [];
    for (let i = 0; i < selectedBoxRows.length; i++) {
      for (let j = 0; j < allOrderList.length; j++) {
        if (selectedBoxRows[i] === allOrderList[j]._id) {
          boxDetails.push(allOrderList[j]);
        }
      }
    }
    setCallModal(
      <AssignDriverBoxesModal
        onSubmit={handleAssignDriver}
        vehicleOptions={vehicleOptions}
        boxInfo={boxDetails}
        handleCancel={handleResetModalState}
      />,
    );
    setModal({ open: true });
  };

  const initialFilter: any = {
    customerOrgList: "",
    sortingHub: "",
    handoverPoint: "",
    pickupType: "",
    status: "",
  };

  const handleAssignDriver = (input) => {
    onAssignDriver(input);
    handleResetModalState();
  };

  const [filterState, setFilterState] = useState<{ [key: string]: string | boolean }>(
    initialFilter,
  );

  useEffect(() => {
    if (onFilter && !isEmpty(filterState)) {
      const filterPayload = omitBy(filterState, (input) => isNil(input) || isEmpty(input));
      onFilter(filterPayload);
    }
  }, [filterState]);

  const initialFilterSelection = [
    {
      key: "customerOrgList",
      name: "customerOrgList",
      label: t("placeholder.filterCustomer"),
      placeholder: t("placeholder.filterCustomer"),
      value: filterState?.customerOrgList,
      options: [{ label: t("option.all"), value: "" }].concat(
        customerOptions?.map((org) => {
          return {
            label: org.name || "",
            value: org._id,
          };
        }),
      ),
    },
    {
      key: "sortingHub",
      name: "sortingHub",
      label: t("placeholder.byOriginHub"),
      placeholder: t("placeholder.byOriginHub"),
      value: filterState?.sortingHub,
      options: [{ label: t("option.all"), value: "" }].concat(
        sortingHubOptions?.map((hub) => {
          return {
            label: hub.locationName || "",
            value: hub._id,
          };
        }),
      ),
    },
    {
      key: "pickupType",
      name: "pickupType",
      label: t("placeholder.byPickup"),
      placeholder: t("placeholder.byPickup"),
      value: filterState?.pickupType,
      options: [
        { label: t("option.all"), value: "" },
        { label: t("option.customer"), value: "CUSTOMER" },
        { label: t("option.3rdParty"), value: "THIRD_PARTY" },
        { label: t("option.diolko"), value: "DRIVER" },
      ],
    },
    {
      key: "status",
      name: "status",
      label: t("placeholder.byStatus"),
      placeholder: t("placeholder.byStatus"),
      value: filterState?.status,
      options: [
        { label: t("option.all"), value: "" },
        { label: t("option.filling"), value: "FILLING" },
        { label: t("option.filled"), value: "FILLED" },
        { label: t("option.loaded"), value: "LOADED" },
        { label: t("option.boarded"), value: "BOARDED" },
        { label: t("option.disembarked"), value: "DISEMBARKED" },
        { label: t("option.received"), value: "RECEIVED" },
        { label: t("option.onHold"), value: "ON_HOLD" },
        { label: t("option.unloaded"), value: "UNLOADED" },
        { label: t("option.closed"), value: "CLOSED" }
      ],
    },
    {
      key: "handoverPoint",
      name: "handoverPoint",
      label: t("label.handoverPoint"),
      placeholder: t("label.handoverPoint"),
      value: filterState?.handoverPoint,
      options: [{ label: t("option.all"), value: "" }].concat(
        handoverPointOptions?.map((place) => {
          return {
            label: place?.name || "",
            value: place?.id,
          };
        }),
      ),
    },
  ];

  const [filterSelection, setFilterSelection] = useState<any>(initialFilterSelection);

  useEffect(() => {
    if (!isEmpty(driverOptions)) setFilterSelection && setFilterSelection(initialFilterSelection);
  }, [driverOptions]);

  // useEffect(() => {
  //   setFilterSelection(initialFilterSelection);
  // }, [handoverPointOptions]);

  const columns: any = [
    {
      title: t("label.originHub").toLocaleUpperCase(),
      dataIndex: "sortingHubData",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <div className="align-flex-end ">
            {/* <span className="half-square custom-icon" style={{ borderTop: `12px solid transparent` }}>&nbsp;</span> */}
            <span
              className="half-square custom-icon mb-5"
              style={{ borderLeft: `12px solid transparent`, borderBottom: `12px solid #E8A51A` }}
            >
              &nbsp;
            </span>
            <span>&nbsp;{record.sortingHubData?.name || "Something wrong"}</span>
          </div>
        );
      },
    },
    {
      title: t("label.batch").toLocaleUpperCase(),
      dataIndex: ["batchCodes"],
      width: 150,
      ellipsis: true,
      render: (items: any, record: any) => {
        if (!isArray(items)) {
          // items can be a string or an array of strings
          // -> backward compatibility, where previous it is a string
          const { batchCode } = record;
          if (batchCode) return batchCode;

          return "";
        }

        return items.map((a, i) => <div key={i}>{a}</div>);
      },
    },
    {
      title: t("label.rack").toLocaleUpperCase(),
      dataIndex: "rackCode",
      ellipsis: true,
      render: (text: any, record: any) => {
        if (!text) return;

        const noPrefixStr = text.substring(4);
        const rackName = record.rackName ?? "-";

        return (
          <div>
            <div className="sbl-tb-title">{rackName}</div>
            <div className="sbl-tb-subtitle">{noPrefixStr}</div>
          </div>
        );
      },
    },
    {
      title: t("label.box").toLocaleUpperCase(),
      dataIndex: "boxCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        const unscanned = (parcel) => parcel?.status === "DROPPED_OFF";

        return (
          <div className="box-code-container">
            <a className="clickable-id">{record.boxCode || ""}</a>
            {record?.parcelList?.some(unscanned) && (
              <Tooltip title="At least one parcel is not scanned.">
                <ExclamationCircleOutlined />
              </Tooltip>
            )}
          </div>
        );
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenBoxesTrackingView(record._id);
          },
        };
      },
    },
    {
      title: t("label.noOfParcels").toLocaleUpperCase(),
      dataIndex: "noOfParcels",
      width: 100,
      ellipsis: true,
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      width: 105,
      ellipsis: true,
      render: (text: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : ""}
          </span>
        );
      },
    },
    {
      title: t("label.date").toLocaleUpperCase(),
      dataIndex: "createdAt",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.createdAt ? (
          <span>{record.createdAt?.substr(0, record.createdAt.indexOf("T"))}</span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.handoverPoint").toLocaleUpperCase(),
      dataIndex: ["handoverPointData", "name"],
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.pickup").toLocaleUpperCase(),
      dataIndex: "deliveryType",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        if (record.deliveryType == "End To End" || record.deliveryType == "Mid-Mile + Last-Mile") {
          return (
            <span>
              {record.assignedDriverData !== null && record.assignedDriverData !== undefined
                ? "DIOLKO Driver"
                : "Driver"}
            </span>
          );
        } else {
          return <span>Customer</span>;
        }
      },
    },
    // {
    //   title: t("label.company").toLocaleUpperCase(),
    //   dataIndex: "uniqueCustomers",
    //   width: 105,
    //   ellipsis: true,
    //   render: (_: any, record: any) => {
    //     return (<span>
    //       {record.uniqueCustomers.map(customer => {
    //         return (
    //           <span>{customer.name}</span>
    //         )
    //       })}
    //     </span>)
    //   }
    // },
    {
      title: t("label.weightInKg").toLocaleUpperCase(),
      dataIndex: "totalParcelWeight",
      ellipsis: true,
      render: (totalParcelWeight: string) => {
        const roundedWeight = parseFloat(totalParcelWeight).toFixed(2);
        return <span>{roundedWeight}</span>;
      },
    },
    {
      title: t("label.weightLoadFactor").toLocaleUpperCase(),
      dataIndex: "weightLoadFactor",
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.volume").toLocaleUpperCase(),
      dataIndex: "totalParcelVolume",
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{parseInt(record.totalParcelVolume).toLocaleString()}</span>;
      },
    },
    {
      title: t("label.volLoadFactor").toLocaleUpperCase(),
      dataIndex: "volumeLoadFactor",
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.driverName").toLocaleUpperCase(),
      dataIndex: ["assignedDriverData"],
      width: 105,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>
            {!isNull(record.assignedDriverData)
              ? record.assignedDriverData?.fullName
              : record.assignedDriver || "Unassigned"}
          </span>
        );
      },
    },
  ];

  const handleSearch = async (text: string) => {
    setFilterState((prevFilter) => ({ ...prevFilter, orFields: text }));
  };

  const handleFilterSelection = (selection) => {
    setFilterState((prevFilter) => ({
      ...prevFilter,
      customerOrgList: selection.customerOrgList,
      sortingHub: selection.sortingHub,
      pickupType: selection.pickupType,
      status: selection.status,
      handoverPoint: selection.handoverPoint,
    }));
  };

  return (
    <>
      <div className="parcel-order-list">
        <DataTable
          rowKey={(record: any) => {
            return record._id;
          }}
          scroll={{ x: true }}
          rowSelection={rowSelection}
          columns={columns}
          dataSource={allOrderList || []}
          title={() => (
            <div>
              <div className="filter-component">
                <DataTableFilter
                  searchPlaceholder={t("placeholder.searchByBoxOrDriver")}
                  onSearch={(text: string) => {
                    handleSearch(text);
                  }}
                  onFilter={(selection) => handleFilterSelection(selection)}
                  renderFilterSelection={filterSelection}
                />
                {roleTypes == "COMPANY-SUPER_ADMIN" ? (
                  <div className="mt-10 mb-20">
                    <Button
                      isDisabled={!selectedBoxRows.length}
                      // isLoading={status === "loading"}
                      type="primary"
                      label={t("action.assignDriver")}
                      size="large"
                      onClick={handleOpenAssignDriver}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
              <Row className="flex-space-between">
                <div>
                  <h4>Total Boxes: {allOrderList.length} </h4>
                </div>
                <div>
                  <h4>
                    <a onClick={handleBoxTrolleyPage}>{t("label.fullscreen")} </a>
                  </h4>
                </div>
              </Row>
            </div>
          )}
        />
      </div>
      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        {callModal}
      </BasicModal>
    </>
  );
};

export default SortingBoxesList;
